import { Box, Button, spacing } from "@procore/core-react";
import { useMemo } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden, useLazyHidden } from "contexts/hidden";
import { useLazyHighlighted } from "contexts/highlighted";
import { useLazySelectionBasket } from "contexts/selectionBasket";
import { collectPdfHiddenIds, usePdfObjectIds } from "hooks/useObjectTreeHidden";
import { useSceneTitle } from "hooks/useSceneTitle";
import { selectInitHomeViewpointId, selectInitViewpoint, selectProcoreContext, selectSceneId } from "slices/explorer";
import { showObjects } from "utils/objectActions";

import { CopyButton, IdRow, Section } from "./common";
import { buildDebugReport } from "./debugReport";

export function SceneSection({ procoreOrigin }: { procoreOrigin: string | undefined }) {
    const sceneId = useAppSelector(selectSceneId);
    const procoreContext = useAppSelector(selectProcoreContext);
    const homeViewpointId = useAppSelector(selectInitHomeViewpointId);
    const initViewpoint = useAppSelector(selectInitViewpoint);
    const { title: sceneTitle } = useSceneTitle();

    return (
        <Section
            title="Scene"
            action={
                <CopyButton
                    label="scene IDs"
                    doneText="Scene context copied"
                    getText={() =>
                        JSON.stringify(
                            {
                                origin: procoreOrigin,
                                sceneTitle: sceneTitle || undefined,
                                companyId: procoreContext?.companyId,
                                projectId: procoreContext?.projectId,
                                sceneId: sceneId ?? undefined,
                                sceneVersionId: procoreContext?.sceneVersionId,
                                sceneSliceId: procoreContext?.sceneSliceId,
                                homeViewpointId: homeViewpointId ?? undefined,
                                viewpointId: initViewpoint?.id,
                            },
                            null,
                            2,
                        )
                    }
                />
            }
        >
            <Box>
                <IdRow label="Company ID" value={procoreContext ? String(procoreContext.companyId) : undefined} />
                <IdRow label="Project ID" value={procoreContext ? String(procoreContext.projectId) : undefined} />
                <IdRow label="Scene ID" value={sceneId ?? undefined} />
                <IdRow label="Version ID" value={procoreContext?.sceneVersionId} />
                {procoreContext?.sceneSliceId && <IdRow label="Slice ID" value={procoreContext.sceneSliceId} />}
                {initViewpoint && <IdRow label="Viewpoint ID" value={initViewpoint.id} />}
                {homeViewpointId && <IdRow label="Home viewpoint ID" value={homeViewpointId} />}
            </Box>
            <Box style={{ display: "flex", gap: spacing.xs }}>
                <ShowPdfsButton />
                <CopyDebugReportButton />
            </Box>
        </Section>
    );
}

/** Unhides every PDF/drawing sheet. Disabled while PDF discovery is outstanding or none are hidden. */
function ShowPdfsButton() {
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const { idArr: hiddenIds } = useHidden();
    const pdfObjectIds = usePdfObjectIds();
    const hiddenPdfIds = useMemo(
        () => (pdfObjectIds ? collectPdfHiddenIds(hiddenIds, pdfObjectIds) : []),
        [hiddenIds, pdfObjectIds],
    );

    return (
        <Button
            size="sm"
            variant="secondary"
            disabled={hiddenPdfIds.length === 0}
            onClick={() => showObjects(hiddenPdfIds, { dispatch, dispatchHidden })}
        >
            {`Unhide PDFs (${pdfObjectIds ? hiddenPdfIds.length : "…"})`}
        </Button>
    );
}

function CopyDebugReportButton() {
    const {
        state: { webviewer },
    } = useExplorerGlobals();
    const store = useAppStore();
    const highlighted = useLazyHighlighted();
    const hidden = useLazyHidden();
    const basket = useLazySelectionBasket();

    return (
        <CopyButton
            label="debug report"
            disabled={!webviewer}
            getText={async () =>
                JSON.stringify(
                    await buildDebugReport({
                        state: store.getState(),
                        webviewer: webviewer!,
                        visibility: {
                            highlightedIds: highlighted.current.idArr,
                            hiddenCount: hidden.current.idArr.length,
                            basketCount: basket.current.idArr.length,
                        },
                    }),
                    null,
                    2,
                )
            }
        >
            Copy debug report
        </CopyButton>
    );
}
